// =============================================================================
// UnitHistorianWidget — the most-recently-touched unit and its voltage story.
//
// Renders the unit as a cobalt hero card (click → open the unit), then a
// small inline voltage chart of its history (last 12 readings), then the
// recent timeline beats. Tells the unit's story without leaving the
// dashboard.
// =============================================================================
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useBatteryUnits, mostRecentlyTouched, relativeWhen, unitName, voltageBucket,
  authorName,
} from "../lib/useBatteryData.js";

import { useAuth } from "../auth/AuthProvider.jsx";

export function UnitHistorianWidget({ config }) {
  const nav = useNavigate();
  const { profile } = useAuth();
  const { data: units, loading } = useBatteryUnits();
  const authorOpts = useMemo(
    () => ({ self: { uid: profile?.uid, displayName: profile?.displayName } }),
    [profile?.uid, profile?.displayName]
  );

  const { unit, history } = useMemo(() => {
    const u = mostRecentlyTouched(units);
    if (!u) return { unit: null, history: [] };
    const h = (Array.isArray(u.history) ? [...u.history] : []).slice(-12).reverse();
    return { unit: u, history: h };
  }, [units]);

  return (
    <div className="w-shell">
      <header className="w-head">
        <div className="w-eye"><span className="w-eye-dot" />UNIT HISTORIAN · MOST RECENT</div>
        <h3 className="w-h3">The unit you <em>touched last</em>.</h3>
      </header>

      {loading && <div className="w-loading">Loading…</div>}

      {!loading && !unit && (
        <div className="w-empty">
          <div className="w-empty-h">No unit activity yet.</div>
          <div className="w-empty-s">Check in a unit and the historian narrates the rest.</div>
        </div>
      )}

      {!loading && unit && (
        <div className="hist-content">
          <button
            type="button"
            className="hist-card"
            onClick={() => nav(`/check-in/${unit.id}`)}
          >
            <div className="hist-card-eye">
              {unit.voltageType === "HV" ? "HV BATTERY" : (Number.isFinite(Number(unit.voltage)) ? `${Number(unit.voltage).toFixed(2)}V · ${voltageBucket(unit).toUpperCase()}` : "NO READING YET")}
            </div>
            <div className="hist-card-name">{unitName(unit)}</div>
            <div className="hist-card-sub">{unit.stockNumber || unit.id.slice(0, 8)}{unit.vin ? ` · VIN ${unit.vin}` : ""}</div>
          </button>

          {history.length >= 2 && <VoltageSpark history={[...history].reverse()} />}

          {history.length === 0 ? (
            <div className="hist-empty">No history on this unit yet.</div>
          ) : (
            <div className="hist-timeline">
              {history.slice(0, 5).map((e, i) => (
                <div key={i} className="hist-row">
                  <div className="hist-bullet" />
                  <div>
                    <div className="hist-when">{relativeWhen(e.at)}</div>
                    <div className="hist-what">
                      <b>{authorName(e.by, authorOpts)}</b> {actionLabel(e.action)}
                      {Number.isFinite(Number(e.voltage)) && (
                        <> · <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{Number(e.voltage).toFixed(2)}V</span></>
                      )}
                      {e.notes && <span style={{ display: "block", marginTop: 2, color: "var(--text-dim)" }}>&ldquo;{String(e.notes).slice(0, 80)}&rdquo;</span>}
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" className="hist-cta" onClick={() => nav(`/check-in/${unit.id}`)}>Open the unit →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Compact inline SVG voltage line — older → newer left to right.
function VoltageSpark({ history }) {
  const points = history
    .map((e) => Number(e.voltage))
    .filter((v) => Number.isFinite(v));
  if (points.length < 2) return null;
  const min = Math.min(...points, 11.8);
  const max = Math.max(...points, 12.8);
  const range = (max - min) || 1;
  const W = 280, H = 56, P = 4;
  const step = (W - P * 2) / (points.length - 1);
  const path = points.map((v, i) => {
    const x = P + i * step;
    const y = H - P - ((v - min) / range) * (H - P * 2);
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return (
    <div style={{ margin: "10px 0 14px", background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 10, padding: 8 }}>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
        <line x1={P} y1={H - P - ((12.4 - min) / range) * (H - P * 2)} x2={W - P} y2={H - P - ((12.4 - min) / range) * (H - P * 2)} stroke="var(--line-2)" strokeDasharray="3,3" />
        <path d={path} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((v, i) => {
          const x = P + i * step;
          const y = H - P - ((v - min) / range) * (H - P * 2);
          const color = v < 12.0 ? "#7C2935" : v < 12.4 ? "#B05E13" : "#2E5694";
          return <circle key={i} cx={x} cy={y} r="2.6" fill={color} />;
        })}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--text-dim)", marginTop: 4, fontFamily: "ui-monospace, Menlo, monospace" }}>
        <span>{points[0].toFixed(2)}V</span>
        <span>{points[points.length - 1].toFixed(2)}V</span>
      </div>
    </div>
  );
}

// Shared author resolution — single source of truth in useBatteryData.js.
// Imported above as `authorName`. Kept here as a stub so the JSX continues
// to call `shortName(...)` without churn.
const shortName = authorName;
function actionLabel(action) {
  const a = String(action || "").toLowerCase();
  if (a.includes("charge")) return "started a charge";
  if (a.includes("remove")) return "removed from charger";
  if (a.includes("intake") || a.includes("add")) return "added the unit";
  if (a.includes("note") || a.includes("comment")) return "noted";
  return "checked it";
}
